import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { apiService } from '../../services/apiService';
import InlineSpinner from '../common/InlineSpinner';
import MetricsGrid from './MetricsGrid';
import styles from './UpdateDetailModal.module.css';

interface UpdateDetailModalProps {
  updateId: string;
  onClose: () => void;
}

interface InsightSummary {
  headline: string;
  narrative: string;
  key_findings: string[];
  recommendations: string[];
  confidence_score: number;
}

interface UpdateDetail {
  id: string;
  company_name: string;
  email_subject: string;
  email_body: string;
  notified_at: string;
  metrics?: Array<{
    label: string;
    value: string;
    trend?: 'up' | 'down' | 'neutral';
    delta?: string;
    category: string;
    icon?: string;
  }>;
  highlights?: string[];
  insights?: Array<{
    id: string;
    title: string;
    summary?: InsightSummary;
    category?: string;
    type?: string;
    data_sources?: string[];
    time_range_start?: string;
    time_range_end?: string;
    primary_dataset?: {
      values: number[];
      labels: string[];
    };
    chart_type?: string;
  }>;
}

// Integration icon mapping
const INTEGRATION_ICONS: Record<string, string> = {
  stripe: '💰',
  github: '🐙',
  gitlab: '🦊',
  slack: '💬',
  intercom: '🗨️',
  quickbooks: '📗',
  xero: '📘',
  mercury: '🪙',
  brex: '💳',
  linear: '📐',
  jira: '📋',
  notion: '📝',
  posthog: '🦔',
  amplitude: '📈',
  mixpanel: '🎛️',
  segment: '🔀',
  heap: '📊',
  hubspot: '🧡',
  salesforce: '☁️',
  pipedrive: '🔧',
  close: '📞',
  attio: '✨',
  zendesk: '🎧',
  ramp: '📊',
  plaid: '🔗',
  chargebee: '🔄',
  paddle: '🏓',
  recurly: '♻️',
};

const getIntegrationIcon = (source: string): string => {
  const normalized = source.toLowerCase().trim();
  return INTEGRATION_ICONS[normalized] || '🔌';
};

const getIntegrationName = (source: string): string => {
  // Check if it's a UUID (contains dashes and is 36 chars long)
  if (source.includes('-') && source.length === 36) {
    // It's a UUID, return empty string to filter it out
    return '';
  }
  
  // Try to extract readable name from integration ID or use as-is
  const parts = source.split('_');
  const name = parts[parts.length - 1] || source;
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const UpdateDetailModal: React.FC<UpdateDetailModalProps> = ({ updateId, onClose }) => {
  const [update, setUpdate] = useState<UpdateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'content' | 'insights'>('content');

  useEffect(() => {
    loadUpdateDetails();
  }, [updateId]);

  const loadUpdateDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch update details from the API
      const response = await apiService.get(`/api/vc/portfolio/updates/${updateId}`);
      if (response.data.success) {
        const updateData = response.data.data;
        console.log('Update data received:', updateData);
        if (updateData.insights) {
          console.log('Insights data:', JSON.stringify(updateData.insights, null, 2));
        }
        setUpdate(updateData);
      } else {
        setError('Failed to load update details');
      }
    } catch (err: any) {
      console.error('Failed to load update details:', err);
      setError(err.message || 'Failed to load update details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
        <div className={styles.modal}>
          <div className={styles.loadingContainer}>
            <InlineSpinner />
            <span>Loading update details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !update) {
    return (
      <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
        <div className={styles.modal}>
          <div className={styles.errorContainer}>
            <div className={styles.errorIcon}>⚠️</div>
            <h3>Failed to Load Update</h3>
            <p>{error || 'Update not found'}</p>
            <button className={styles.closeButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modalBackdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.companyInfo}>
              <div className={styles.companyIcon}>
                {update.company_name.charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className={styles.companyName}>{update.company_name}</h2>
                <p className={styles.updateDate}>
                  {new Date(update.notified_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
            <button className={styles.closeIcon} onClick={onClose}>
              ✕
            </button>
          </div>
          
          <h3 className={styles.emailSubject}>{update.email_subject}</h3>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'content' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('content')}
          >
            Email Content
          </button>
          {update.insights && update.insights.length > 0 ? (
            <button
              className={`${styles.tab} ${activeTab === 'insights' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('insights')}
            >
              Insights ({update.insights.length})
            </button>
          ) : (
            <div className={styles.noInsightsIndicator}>
              No insights attached
            </div>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {activeTab === 'content' ? (
            <div className={styles.emailContent}>
              <div 
                className={styles.emailBody}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.email_body || '') }}
              />
              
              {/* Highlights */}
              {update.highlights && update.highlights.length > 0 && (
                <div className={styles.highlightsSection}>
                  <h4 className={styles.sectionTitle}>Key Highlights</h4>
                  <div className={styles.highlightsList}>
                    {update.highlights.map((highlight, idx) => (
                      <div key={idx} className={styles.highlightItem}>
                        <span className={styles.highlightBullet}>•</span>
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.insightsContent}>
              {update.insights && update.insights.length > 0 ? (
                update.insights.map((insight) => {
                  console.log('Rendering insight:', insight.id, insight);
                  
                  // Handle different summary structures
                  const summary = insight.summary;
                  const hasTimeRange = insight.time_range_start && insight.time_range_end;
                  
                  // Filter out UUIDs from data sources
                  const cleanSources = insight.data_sources
                    ?.map(source => getIntegrationName(source))
                    .filter(name => name !== '') || [];
                  const hasSources = cleanSources.length > 0;
                  
                  // Check for findings and recommendations
                  const hasFindings = summary?.key_findings && Array.isArray(summary.key_findings) && summary.key_findings.length > 0;
                  const hasRecommendations = summary?.recommendations && Array.isArray(summary.recommendations) && summary.recommendations.length > 0;
                  
                  const confidenceScore = summary?.confidence_score || 0;
                  const confidencePercent = Math.round(confidenceScore * 100);
                  const confidenceLabel = confidenceScore >= 0.8 ? 'High' 
                    : confidenceScore >= 0.6 ? 'Medium' 
                    : confidenceScore > 0 ? 'Low' : '';
                  
                  // Check for chart data in primary_dataset
                  const primaryDataset = insight.primary_dataset as any;
                  const hasChartData = primaryDataset && (
                    (primaryDataset.values && primaryDataset.values.length > 1) ||
                    (primaryDataset.data_points && primaryDataset.data_points.length > 1)
                  );
                  
                  // Determine if we have any meaningful content to show
                  const hasContent = summary?.headline || summary?.narrative || 
                                    hasFindings || hasRecommendations || 
                                    hasTimeRange || hasSources || hasChartData;
                  
                  return (
                    <div key={insight.id} className={styles.enhancedInsightCard}>
                      {/* Header */}
                      <div className={styles.insightHeader}>
                        <h4 className={styles.insightTitle}>{insight.title}</h4>
                        {insight.category && (
                          <span className={`${styles.insightBadge} ${styles[`badge${insight.category.charAt(0).toUpperCase()}${insight.category.slice(1)}`]}`}>
                            {insight.category}
                          </span>
                        )}
                      </div>
                      
                    {/* Meta Info - Time Range & Data Sources */}
                    {(hasTimeRange || hasSources) && (
                      <div className={styles.insightMeta}>
                        {hasTimeRange && (
                          <span className={styles.timeRange}>
                            {new Date(insight.time_range_start!).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })} - {new Date(insight.time_range_end!).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        )}
                        {hasSources && (
                          <span className={styles.dataSources}>
                            {cleanSources.join(' + ')}
                          </span>
                        )}
                      </div>
                    )}
                      
                      {/* Headline (if different from title) */}
                      {summary?.headline && summary.headline !== insight.title && (
                        <div className={styles.insightHeadline}>
                          {summary.headline}
                        </div>
                      )}
                      
                      {/* Narrative */}
                      {summary?.narrative && (
                        <div className={styles.insightNarrative}>
                          {summary.narrative}
                        </div>
                      )}
                      
                    {/* Key Findings */}
                    {hasFindings && (
                      <div className={styles.findingsSection}>
                        <h5 className={styles.findingsSectionTitle}>Key Findings</h5>
                        <ul className={styles.findingsList}>
                          {summary!.key_findings.map((finding, idx) => (
                            <li key={idx} className={styles.findingItem}>{finding}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Recommendations */}
                    {hasRecommendations && (
                      <div className={styles.recommendationsSection}>
                        <h5 className={styles.recommendationsSectionTitle}>Recommendations</h5>
                        <ul className={styles.recommendationsList}>
                          {summary!.recommendations.map((rec, idx) => (
                            <li key={idx} className={styles.recommendationItem}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Footer - Chart */}
                    {hasChartData && (
                      <div className={styles.insightFooter}>
                        <span className={styles.chartAvailable}>
                          Chart data available {insight.chart_type && `(${insight.chart_type})`}
                        </span>
                      </div>
                    )}
                      
                      {/* Debug info in development */}
                      {!hasContent && (
                        <div className={styles.insightDebug}>
                          <em>Insight data structure:</em>
                          <pre>{JSON.stringify(insight, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className={styles.noInsights}>
                  <p>No insights available for this update.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateDetailModal;

